import React from 'react';
import {
  required, FormDataConsumer, List, SaveButton, Datagrid, DateField, TextField,
  Edit, Create, FormTab, TabbedForm, Toolbar, TextInput, EditButton, ReferenceInput,
  ReferenceField, AutocompleteInput, Filter, NumberInput, Labeled, FunctionField,
} from 'react-admin';
import { Box } from '@material-ui/core';
import { CsvUploadComponent } from './components/CsvUploadComponent';
import { IpListDeleteButton } from './components/IpListDeleteButton';

const { SectionHeader, formatDate } = require('./components/utils.js');

const twoColumnGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px', width: '50%' };
const fullWidth = { gridColumn: '1 / -1' };

function isSuperAdmin(permissions) {
  return permissions && permissions.role === 'super_admin';
}

function isAdminRole(permissions) {
  return permissions && (permissions.role === 'admin' || permissions.role === 'super_admin');
}

const IpListTitle = ({ record }) => (
  <span>IP List {record ? `"${record.name}"` : ''}</span>
);

const UpdatedAtField = ({ record }) => {
  if (!record || !record.updated_at) return <span></span>;
  return <span><nobr>{formatDate(record.updated_at)}</nobr></span>;
};
UpdatedAtField.defaultProps = { addLabel: true, label: 'Updated' };

const IpListFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="search_name" alwaysOn resettable />
    <NumberInput label="List ID" source="search_id" alwaysOn resettable />
  </Filter>
);

export const IpListList = ({ permissions, ...props }) => (
  <List
    {...props}
    filters={<IpListFilter />}
    perPage={25}
    bulkActionButtons={false}
  >
    <Datagrid rowClick="edit">
      <TextField source="code" label="ID" />
      <TextField source="name" label="Name" />
      <TextField source="description" label="Description" />
      {isSuperAdmin(permissions) ? (
        <ReferenceField source="organization_id" label="Organization" reference="organizations" link={false} allowEmpty>
          <TextField source="name" />
        </ReferenceField>
      ) : (
        <FunctionField label="Organization" render={record => record && record.organization_id == null ? 'Global' : '—'} />
      )}
      <ReferenceField source="advertiser_id" label="Advertiser" reference="advertisers" link={false} allowEmpty>
        <TextField source="name" />
      </ReferenceField>
      <UpdatedAtField label="Updated" />
      <EditButton variant="contained" color="primary" />
      <IpListDeleteButton resource="ip_lists" />
    </Datagrid>
  </List>
);

const CustomToolbar = ({ permissions, ...props }) => (
  <Toolbar {...props}>
    <SaveButton {...props} submitOnEnter={false} />
    <DateField
      source="updated_at"
      label="Update date"
      style={{ padding: '10px' }}
      showTime
    />
    <TextField source="updated_name" label="" style={{ padding: '10px' }} />
  </Toolbar>
);

/**
 * Shared "Settings" tab body. Used by both Create and Edit; Edit additionally
 * shows read-only `code` and `updated_at`.
 */
const SettingsTabFields = ({ permissions, isEdit }) => (
  <Box>
    <SectionHeader title="General" />
    {isEdit && (
      <Labeled label="List ID">
        {/* TextField, not NumberField: a code is an identifier, and NumberField
            applies locale grouping, so list 11105 renders as "11,105". Matches
            the ops publisher and site forms. */}
        <TextField source="code" style={{ fontSize: '1rem' }} />
      </Labeled>
    )}
    <br/>
    <TextInput source="name" label="Name" validate={required()} style={{ maxWidth: 256 }} />
    <Box style={fullWidth}>
      <TextInput source="description" label="Description" multiline style={{width: '50%'}}/>
    </Box>

    <SectionHeader title="Ownership" />
    {isSuperAdmin(permissions) ? (
      <ReferenceInput
        label="Organization (leave empty for global)"
        source="organization_id"
        reference="organizations"
        allowEmpty
        emptyText="- Global (visible to all orgs) -"
        perPage={100}
        sort={{ field: 'name', order: 'ASC' }}
      >
        <AutocompleteInput optionText="name" optionValue="code" style={{ maxWidth: 256 }} />
      </ReferenceInput>
    ) : (
      <Labeled label="Organization">
        <FormDataConsumer>
          {({ formData }) => (
            <TextField
              record={{ orgLabel: formData && formData.organization_id == null
                ? 'Global (visible to all orgs)'
                : 'Your organization' }}
              source="orgLabel" />
          )}
        </FormDataConsumer>
      </Labeled>
    )}
    {isAdminRole(permissions) && (
      <ReferenceInput
        label="Advertiser (leave empty for an ops-only list)"
        source="advertiser_id"
        reference="advertisers"
        allowEmpty
        emptyText="- Ops only (no advertiser can see it) -"
        perPage={100}
        sort={{ field: 'name', order: 'ASC' }}
      >
        <AutocompleteInput optionText="name" optionValue="code" style={{ maxWidth: 256 }} />
      </ReferenceInput>
    )}
  </Box>
);

/**
 * Shared "Upload" tab body. Wrapped in a FormDataConsumer so the caller can
 * gate it on whether the record has been created yet (formData.code present).
 */
const UploadTabFields = () => (
  <FormDataConsumer>
    {({ formData }) => (
      <Box style={{ width: '100%' }}>
        <SectionHeader title="CSV Upload" />
        <CsvUploadComponent
          source="file_upload"
          label="Upload CSV"
          existingKey={formData && formData.spaces_key}
          code={formData && formData.code}
        />
      </Box>
    )}
  </FormDataConsumer>
);

export const IpListCreate = ({ permissions, ...props }) => (
  <Create title="Create IP List" mutationMode="pessimistic" {...props}>
    <TabbedForm toolbar={<CustomToolbar {...props} permissions={permissions} />}>
      <FormTab label="Settings">
        <SettingsTabFields permissions={permissions} isEdit={false} />
      </FormTab>

      {/* Upload tab is only useful after the record has been created
          (we need a `code` to name the Spaces object). Until then, hide it. */}
      <FormDataConsumer>
        {({ formData, ...rest }) =>
          formData && formData.code ? (
            <FormTab label="Upload" {...rest}>
              <UploadTabFields />
            </FormTab>
          ) : null
        }
      </FormDataConsumer>
    </TabbedForm>
  </Create>
);

export const IpListEdit = ({ permissions, ...props }) => (
  <Edit title={<IpListTitle />} mutationMode="pessimistic" {...props}>
    <TabbedForm toolbar={<CustomToolbar {...props} permissions={permissions} />}>
      <FormTab label="Settings">
        <SettingsTabFields permissions={permissions} isEdit={true} />
      </FormTab>
      <FormTab label="Upload">
        <UploadTabFields />
      </FormTab>
    </TabbedForm>
  </Edit>
);
